(() => {
  const banner = document.querySelector('[data-ic-consent]');
  if (!banner) return;

  const preferences = banner.querySelector('[data-ic-consent-preferences]');
  const summary = banner.querySelector('[data-ic-consent-summary]');
  const manageButton = banner.querySelector('[data-ic-consent-manage]');
  const closeButton = banner.querySelector('[data-ic-consent-close]');
  const status = banner.querySelector('[data-ic-consent-status]');
  const fields = Object.fromEntries(
    [...banner.querySelectorAll('[data-ic-consent-field]')].map(input => [input.dataset.icConsentField, input])
  );
  let privacyApi;
  let returnFocus;
  let initialized = false;
  let loadAttempts = 0;
  let loadTimer;
  let saving = false;
  const choiceButtons = [...banner.querySelectorAll('[data-ic-consent-accept], [data-ic-consent-reject], [data-ic-consent-save]')];
  const showEverywhere = banner.dataset.showEverywhere === 'true';

  const googleConsent = choices => {
    if (typeof window.gtag !== 'function') return;
    window.gtag('consent', 'update', {
      analytics_storage: choices.analytics ? 'granted' : 'denied',
      ad_storage: choices.marketing ? 'granted' : 'denied',
      ad_user_data: choices.marketing ? 'granted' : 'denied',
      ad_personalization: choices.marketing ? 'granted' : 'denied',
      functionality_storage: choices.preferences ? 'granted' : 'denied',
      personalization_storage: choices.preferences ? 'granted' : 'denied',
      security_storage: 'granted'
    });
  };

  const currentChoices = () => {
    const current = privacyApi.currentVisitorConsent();
    return {
      preferences: current.preferences === 'yes',
      analytics: current.analytics === 'yes',
      marketing: current.marketing === 'yes'
    };
  };

  const syncGoogleConsent = () => {
    if (!privacyApi) return;
    googleConsent({
      preferences: privacyApi.preferencesProcessingAllowed(),
      analytics: privacyApi.analyticsProcessingAllowed(),
      marketing: privacyApi.marketingAllowed()
    });
  };

  const fillChoices = choices => Object.entries(fields).forEach(([name, input]) => {
    input.checked = Boolean(choices[name]);
  });

  const hideBanner = () => {
    banner.hidden = true;
    document.documentElement.classList.remove('ic-consent-is-open');
  };

  const showBanner = () => {
    banner.hidden = false;
    document.documentElement.classList.add('ic-consent-is-open');
  };

  const openPreferences = trigger => {
    returnFocus = trigger || document.activeElement;
    fillChoices(currentChoices());
    summary.hidden = true;
    preferences.hidden = false;
    manageButton?.setAttribute('aria-expanded', 'true');
    showBanner();
    closeButton?.focus();
  };

  const closePreferences = () => {
    preferences.hidden = true;
    summary.hidden = false;
    manageButton?.setAttribute('aria-expanded', 'false');
    const current = privacyApi.currentVisitorConsent();
    const hasUnansweredChoice = ['preferences', 'analytics', 'marketing'].some(name => current[name] === '');
    if (!hasUnansweredChoice || (!showEverywhere && !privacyApi.shouldShowBanner())) hideBanner();
    returnFocus?.focus?.();
  };

  const save = choices => {
    if (saving || !privacyApi) return;
    saving = true;
    choiceButtons.forEach(button => { button.disabled = true; });
    status.textContent = 'Saving your choices…';
    let completed = false;
    const finish = () => {
      saving = false;
      choiceButtons.forEach(button => { button.disabled = false; });
    };
    const failed = () => {
      if (completed) return;
      completed = true;
      window.clearTimeout(saveTimer);
      finish();
      fillChoices(choices);
      summary.hidden = true;
      preferences.hidden = false;
      manageButton?.setAttribute('aria-expanded', 'true');
      showBanner();
      status.textContent = 'Your choices could not be saved. Please try again.';
    };
    const saveTimer = window.setTimeout(failed, 8000);
    try {
      privacyApi.setTrackingConsent(choices, () => {
        if (completed) return;
        const confirmed = currentChoices();
        if (Object.keys(choices).some(name => confirmed[name] !== choices[name])) {
          failed();
          return;
        }
        completed = true;
        window.clearTimeout(saveTimer);
        syncGoogleConsent();
        status.textContent = 'Your cookie choices have been saved.';
        document.dispatchEvent(new CustomEvent('industracare:consent-updated', { detail: choices }));
        window.setTimeout(() => {
          hideBanner();
          preferences.hidden = true;
          summary.hidden = false;
          manageButton?.setAttribute('aria-expanded', 'false');
          status.textContent = '';
          finish();
        }, 450);
      });
    } catch (error) {
      failed();
    }
  };

  const initialize = () => {
    if (initialized) return;
    privacyApi = window.Shopify.customerPrivacy;
    initialized = true;
    window.clearTimeout(loadTimer);
    banner.querySelector('[data-ic-consent-load-error]')?.remove();
    choiceButtons.forEach(button => { button.disabled = false; });
    if (manageButton) manageButton.disabled = false;

    const rawConsent = privacyApi.currentVisitorConsent();
    const existing = currentChoices();
    syncGoogleConsent();
    fillChoices(existing);
    const hasUnansweredChoice = ['preferences', 'analytics', 'marketing'].some(name => rawConsent[name] === '');
    if ((showEverywhere && hasUnansweredChoice) || (!showEverywhere && privacyApi.shouldShowBanner())) showBanner();
    else hideBanner();

    banner.querySelectorAll('[data-ic-consent-accept]').forEach(button => {
      button.addEventListener('click', () => save({ preferences: true, analytics: true, marketing: true }));
    });
    banner.querySelector('[data-ic-consent-reject]')?.addEventListener('click', () => {
      save({ preferences: false, analytics: false, marketing: false });
    });
    banner.querySelector('[data-ic-consent-save]')?.addEventListener('click', () => save({
      preferences: fields.preferences.checked,
      analytics: fields.analytics.checked,
      marketing: fields.marketing.checked
    }));
    manageButton?.addEventListener('click', event => openPreferences(event.currentTarget));
    closeButton?.addEventListener('click', closePreferences);
    document.querySelectorAll('[data-ic-open-consent]').forEach(button => {
      button.addEventListener('click', event => openPreferences(event.currentTarget));
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !banner.hidden && !preferences.hidden) closePreferences();
    });
    // Follow consent changes made by Shopify, including a return from checkout.
    document.addEventListener('visitorConsentCollected', syncGoogleConsent);
    window.addEventListener('pageshow', syncGoogleConsent);
  };

  const showLoadError = () => {
    if (initialized || banner.querySelector('[data-ic-consent-load-error]')) return;
    choiceButtons.forEach(button => { button.disabled = true; });
    if (manageButton) manageButton.disabled = true;
    const message = document.createElement('p');
    message.dataset.icConsentLoadError = '';
    message.className = 'ic-consent__status';
    message.setAttribute('role', 'status');
    message.textContent = 'Cookie settings are temporarily unavailable. ';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'ic-consent__manage';
    retry.textContent = 'Try again';
    retry.addEventListener('click', () => {
      message.remove();
      loadAttempts = 0;
      loadPrivacy();
    });
    message.appendChild(retry);
    summary.appendChild(message);
    showBanner();
  };

  const retryLoad = () => {
    if (initialized) return;
    window.clearTimeout(loadTimer);
    if (loadAttempts >= 6) {
      showLoadError();
      return;
    }
    loadTimer = window.setTimeout(loadPrivacy, Math.min(250 * (2 ** (loadAttempts - 1)), 4000));
  };

  const loadPrivacy = () => {
    if (initialized) return;
    loadAttempts += 1;
    if (!window.Shopify?.loadFeatures) {
      retryLoad();
      return;
    }
    let returned = false;
    loadTimer = window.setTimeout(() => {
      returned = true;
      retryLoad();
    }, 5000);
    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], error => {
        if (returned || initialized) return;
        returned = true;
        window.clearTimeout(loadTimer);
        if (error || !window.Shopify.customerPrivacy) {
          retryLoad();
          return;
        }
        initialize();
      });
    } catch (error) {
      returned = true;
      retryLoad();
    }
  };

  loadPrivacy();
})();
